import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import BalanceCard from '../components/BalanceCard';
import { DailyStatsWithComparison } from '../types';
import { Info } from 'lucide-react';
// @ts-ignore
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { ru as ruLocale } from 'date-fns/locale';

interface Statistics {
  total_balance: number;
  total_devices: number;
  today_income: number;
  monthly_income: number;
}

const periods = [
  { label: '1д', value: 1 },
  { label: '7д', value: 7 },
  { label: '30д', value: 30 },
];

// Кастомный форматтер для Tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#23262F] border border-[#353945] rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium">
          {`${new Date(label).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}`}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value.toFixed(2)} ₽`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Tooltip для выводов
const WithdrawalsTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#23262F] border border-[#353945] rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium">
          {`${new Date(label).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}`}
        </p>
        <p className="text-pink-400">
          {`Вывод: ${payload[0].value.toFixed(2)} ₽`}
        </p>
      </div>
    );
  }
  return null;
};

// Компонент с иконкой информации и подсказкой
const InfoTooltip = ({ children }: { children: React.ReactNode }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top');
  const [tooltipAlign, setTooltipAlign] = useState<'center' | 'left' | 'right'>('center');

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipHeight = 80; // Примерная высота подсказки
    const tooltipWidth = 384; // w-96 = 384px
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    
    // Определяем вертикальную позицию
    if (spaceAbove < tooltipHeight && spaceBelow > tooltipHeight) {
      setTooltipPosition('bottom');
    } else {
      setTooltipPosition('top');
    }
    
    // Определяем горизонтальную позицию
    const centerOffset = tooltipWidth / 2;
    if (rect.left + centerOffset > window.innerWidth) {
      setTooltipAlign('right');
    } else if (rect.left - centerOffset < 0) {
      setTooltipAlign('left');
    } else {
      setTooltipAlign('center');
    }
    
    setShowTooltip(true);
  };

  const getTooltipClasses = () => {
    const baseClasses = "absolute px-3 py-2 bg-[#23262F] border border-[#353945] rounded-lg shadow-lg text-sm text-white w-96 z-50";
    
    let positionClasses = "";
    if (tooltipPosition === 'top') {
      positionClasses = "bottom-full mb-2";
    } else {
      positionClasses = "top-full mt-2";
    }
    
    let alignClasses = "";
    if (tooltipAlign === 'center') {
      alignClasses = "left-1/2 transform -translate-x-1/2";
    } else if (tooltipAlign === 'left') {
      alignClasses = "left-0";
    } else {
      alignClasses = "right-0";
    }
    
    return `${baseClasses} ${positionClasses} ${alignClasses}`;
  };

  const getArrowClasses = () => {
    const baseClasses = "absolute w-0 h-0 border-l-4 border-r-4 border-transparent";
    
    let positionClasses = "";
    if (tooltipPosition === 'top') {
      positionClasses = "top-full border-t-4 border-t-[#23262F]";
    } else {
      positionClasses = "bottom-full border-b-4 border-b-[#23262F]";
    }
    
    let alignClasses = "";
    if (tooltipAlign === 'center') {
      alignClasses = "left-1/2 transform -translate-x-1/2";
    } else if (tooltipAlign === 'left') {
      alignClasses = "left-4";
    } else {
      alignClasses = "right-4";
    }
    
    return `${baseClasses} ${positionClasses} ${alignClasses}`;
  };

  return (
    <div className="relative inline-block">
      <div
        className="inline-flex items-center justify-center w-5 h-5 text-gray-400 hover:text-gray-300 cursor-help transition-colors"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Info size={16} />
      </div>
      {showTooltip && (
        <div className={getTooltipClasses()}>
          <div className="whitespace-normal leading-relaxed">
            {children}
          </div>
          <div className={getArrowClasses()}></div>
        </div>
      )}
    </div>
  );
};

// Компонент с подсказкой для текста (без иконки)
const TextTooltip = ({ children, tooltipText }: { children: React.ReactNode; tooltipText: string }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    setShowTooltip(true);
    setTimeout(() => {
      if (!triggerRef.current || !tooltipRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      let top = triggerRect.top - tooltipRect.height - 8; // 8px отступ сверху
      // Если не помещается сверху — показываем снизу
      if (top < 0) {
        top = triggerRect.bottom + 8;
      }
      // Корректируем если выходит за левую или правую границу
      if (left < 4) left = 4;
      if (left + tooltipRect.width > window.innerWidth - 4) {
        left = window.innerWidth - tooltipRect.width - 4;
      }
      setTooltipStyle({
        position: 'fixed',
        left: left,
        top: top,
        zIndex: 9999,
        pointerEvents: 'none',
      });
    }, 0);
  };

  return (
    <div ref={triggerRef} style={{display: 'inline-block'}}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div
          ref={tooltipRef}
          style={tooltipStyle}
          className="px-3 py-2 bg-[#23262F] border border-[#353945] rounded-lg shadow-lg text-sm text-white w-64 whitespace-normal leading-relaxed text-left"
        >
          {tooltipText}
        </div>
      )}
    </div>
  );
};

// Функция для получения домена дат с отступами
function getDateDomainWithPadding(data: {date: number}[]) {
  if (data.length === 0) return [0, 0];
  
  const minDate = Math.min(...data.map(d => d.date));
  const maxDate = Math.max(...data.map(d => d.date));
  const padding = (maxDate - minDate) * 0.1;
  
  return [minDate - padding, maxDate + padding];
}

// Модифицируем локаль для двухбуквенных дней недели
const customRu = {
  ...ruLocale,
  localize: {
    ...ruLocale.localize,
    day: (n: number, _options?: any) => {
      // 0 - воскресенье, 1 - понедельник ...
      const days = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'];
      return days[n];
    },
  },
};

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingBalances, setUpdatingBalances] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<DailyStatsWithComparison[]>([]);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(7);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null; key: string } | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDatePicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Вычисляем сумму доходов за выбранный период
  const totalIncome = analyticsData.reduce((sum, item) => sum + (item.daily_income || 0), 0);
  
  // Вычисляем сумму выводов за выбранный период
  const totalWithdrawals = analyticsData.reduce((sum, item) => sum + (item.withdrawals || 0), 0);

  useEffect(() => {
    loadData();
  }, []); // Загружаем общую статистику только при монтировании компонента

  useEffect(() => {
    if (!dateRange) {
      loadAnalytics();
    }
  }, [analyticsPeriod]); // Загружаем аналитику только при изменении периода

  useEffect(() => {
    if (dateRange) {
      loadAnalyticsByRange();
    }
  }, [dateRange]);

  const loadData = async (silent: boolean = false) => {
    try {
      if (!silent) setLoading(true);
      const statsData = await apiService.getStatisticsOverview();
      setStatistics(statsData);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const stats = await apiService.getDailyStatisticsWithComparison(analyticsPeriod);
      // Бэкенд теперь сам возвращает полный список дней, включая дни без данных
      setAnalyticsData(stats);
    } catch (e) {
      console.error('Ошибка загрузки аналитики:', e);
      // При ошибке показываем пустые данные
      setAnalyticsData([]);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadAnalyticsByRange = async () => {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) return;
    setAnalyticsLoading(true);
    try {
      const stats = await apiService.getDailyStatisticsByRange(dateRange.startDate, dateRange.endDate);
      setAnalyticsData(stats);
    } catch (e) {
      setAnalyticsData([]);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleUpdateBalances = async () => {
    try {
      setUpdatingBalances(true);
      const result = await apiService.updateAllBalances();
      toast.success(result.message);
      await loadData(true); // Обновляем статистику без глобального спиннера
      await loadAnalytics(); // Перезагружаем данные аналитики
    } catch (error) {
      console.error('Ошибка обновления балансов:', error);
      toast.error('Ошибка обновления балансов');
    } finally {
      setUpdatingBalances(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Вы вышли из системы');
    navigate('/login');
  };

  const handlePeriodClick = (period: number) => {
    setAnalyticsPeriod(period);
    setDateRange(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 bg-[#181A20] min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-wide">Обзор</h1>
          <p className="text-gray-400 mt-2 text-sm">Общий обзор основных показателей</p>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-gray-400">Привет, {user?.first_name}!</span>
          {!(typeof window !== 'undefined' && (window as any).Telegram?.WebApp) && (
            <button
              onClick={handleLogout}
              className="bg-[#23262F] hover:bg-[#353945] text-[#B1B5C3] px-4 py-2 rounded-lg transition-colors"
            >
              Выйти
            </button>
          )}
        </div>
      </div>

      {/* Статистика */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <BalanceCard 
            title="Ваш баланс"
            value={`${statistics.total_balance.toFixed(2)} ₽`}
            onRefreshClick={handleUpdateBalances}
            refreshing={updatingBalances}
          />
          <BalanceCard 
            title="Доход за текущий месяц"
            value={`${statistics.monthly_income.toFixed(2)} ₽`}
          />
          <BalanceCard 
            title="Доход за сегодня"
            value={`${statistics.today_income.toFixed(2)} ₽`}
          />
        </div>
      )}

      {/* Аналитика: общий блок с двумя графиками */}
      <div className="bg-[#181A20] rounded-xl shadow border border-[#23262F] mb-8">
        <div className="px-6 py-4 border-b border-[#23262F] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="text-xl font-semibold text-white">Аналитика</h2>
          <div className="flex items-center gap-4">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => handlePeriodClick(p.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200
                  ${analyticsPeriod === p.value && !dateRange ? 'bg-blue-600 text-white' : 'bg-[#23262F] text-[#B1B5C3] hover:bg-[#353945]'}`}
              >
                {p.label}
              </button>
            ))}
            <button
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${dateRange && dateRange.startDate && dateRange.endDate ? 'bg-blue-600 text-white' : 'bg-[#23262F] text-[#B1B5C3] hover:bg-[#353945]'}`}
              onClick={() => setShowDatePicker((v) => !v)}
            >
              {dateRange && dateRange.startDate && dateRange.endDate
                ? `${dateRange.startDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${dateRange.endDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                : 'Диапазон'}
            </button>
            {showDatePicker && (
              <div ref={datePickerRef} className="absolute z-50 mt-2 right-0">
                <DateRange
                  locale={customRu}
                  onChange={(ranges: { selection: { startDate: Date | null; endDate: Date | null; key: string } }) => {
                    const range = ranges.selection;
                    if (range.startDate && range.endDate && range.startDate.getTime() !== range.endDate.getTime()) {
                      setDateRange({
                        startDate: range.startDate,
                        endDate: range.endDate,
                        key: 'selection',
                      });
                      setShowDatePicker(false);
                    } else {
                      setDateRange({
                        startDate: range.startDate,
                        endDate: range.endDate,
                        key: 'selection',
                      });
                    }
                  }}
                  moveRangeOnFirstSelection={false}
                  ranges={[
                    {
                      startDate: dateRange && dateRange.startDate ? dateRange.startDate : new Date(),
                      endDate: dateRange && dateRange.endDate ? dateRange.endDate : new Date(),
                      key: 'selection',
                    },
                  ]}
                  maxDate={new Date()}
                  rangeColors={["#6366f1"]}
                />
              </div>
            )}
          </div>
        </div>
        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* График Доходы */}
            <div className="w-full md:w-1/2 relative">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-white">Доходы</h3>
                  <InfoTooltip>
                    Исторические данные представлены в соответствии с часовым поясом сервера.
                  </InfoTooltip>
                </div>
                <TextTooltip tooltipText="Рассчитано за весь выбранный период">
                  <div className="text-right cursor-help">
                    <span className="text-gray-400 text-sm">Сумма: </span>
                    <span className="text-green-400 font-semibold">
                      {totalIncome.toFixed(2)} ₽
                    </span>
                  </div>
                </TextTooltip>
              </div>
              <div className="bg-[#23262F] rounded-xl pt-6 pb-4 px-6 relative">
                {analyticsLoading && (
                  <div className="absolute inset-0 bg-[#23262F]/80 flex items-center justify-center z-10 rounded-xl">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                )}
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart 
                    data={analyticsData.map(d => ({ ...d, date: new Date(d.date).getTime() }))} 
                    margin={{ top: 20, right: 30, left: 0, bottom: -20 }}
                  >
                    <CartesianGrid strokeDasharray="0" stroke="#353945" />
                    <XAxis
                      dataKey="date"
                      stroke="#B1B5C3"
                      fontSize={12}
                      domain={analyticsData.length > 1 ? getDateDomainWithPadding(analyticsData.map(d => ({ date: new Date(d.date).getTime() }))) as any : undefined}
                      type="number"
                      scale="time"
                      angle={analyticsPeriod > 15 ? -45 : 0}
                      textAnchor={analyticsPeriod > 15 ? "end" : "middle"}
                      height={analyticsPeriod > 15 ? 80 : 60}
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString('ru-RU', { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis stroke="#B1B5C3" fontSize={12} tickFormatter={(value) => Math.round(value).toString()} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="linear" 
                      dataKey="daily_income" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      fillOpacity={0} 
                      name="Текущий доход"
                      animationDuration={300}
                      dot={{
                        fill: '#10b981',
                        stroke: '#ffffff',
                        strokeWidth: 2,
                        r: 5,
                        strokeDasharray: '0'
                      }}
                      activeDot={{
                        fill: '#ffffff',
                        stroke: '#10b981',
                        strokeWidth: 3,
                        r: 7
                      }}
                    />
                    {analyticsPeriod === 7 && !dateRange && (
                      <Area 
                        type="linear" 
                        dataKey="week_ago_income" 
                        stroke="#6b7280" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        fillOpacity={0} 
                        name="Прошлая неделя"
                        animationDuration={300}
                        dot={{
                          fill: '#6b7280',
                          stroke: '#ffffff',
                          strokeWidth: 2,
                          r: 4,
                          strokeDasharray: '0'
                        }}
                        activeDot={{
                          fill: '#ffffff',
                          stroke: '#6b7280',
                          strokeWidth: 3,
                          r: 6
                        }}
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* График Выводы */}
            <div className="w-full md:w-1/2 relative">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-white">Выводы</h3>
                  <InfoTooltip>
                  Исторические данные представлены в соответствии с часовым поясом сервера.
                  </InfoTooltip>
                </div>
                <TextTooltip tooltipText="Рассчитано за весь выбранный период">
                  <div className="text-right cursor-help">
                    <span className="text-gray-400 text-sm">Сумма: </span>
                    <span className="text-pink-400 font-semibold">
                      {totalWithdrawals.toFixed(2)} ₽
                    </span>
                  </div>
                </TextTooltip>
              </div>
              <div className="bg-[#23262F] rounded-xl pt-6 pb-4 px-6 relative">
                {analyticsLoading && (
                  <div className="absolute inset-0 bg-[#23262F]/80 flex items-center justify-center z-10 rounded-xl">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
                  </div>
                )}
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart 
                    data={analyticsData.map(d => ({ ...d, date: new Date(d.date).getTime() }))} 
                    margin={{ top: 20, right: 30, left: 0, bottom: -20 }}
                  >
                    <CartesianGrid strokeDasharray="0" stroke="#353945" />
                    <XAxis
                      dataKey="date"
                      stroke="#B1B5C3"
                      fontSize={12}
                      domain={analyticsData.length > 1 ? getDateDomainWithPadding(analyticsData.map(d => ({ date: new Date(d.date).getTime() }))) as any : undefined}
                      type="number"
                      scale="time"
                      angle={analyticsPeriod > 15 ? -45 : 0}
                      textAnchor={analyticsPeriod > 15 ? "end" : "middle"}
                      height={analyticsPeriod > 15 ? 80 : 60}
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString('ru-RU', { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis stroke="#B1B5C3" fontSize={12} tickFormatter={(value) => Math.round(value).toString()} />
                    <Tooltip content={<WithdrawalsTooltip />} />
                    <Area 
                      type="linear" 
                      dataKey="withdrawals" 
                      stroke="#ec4899" 
                      strokeWidth={2}
                      fillOpacity={0} 
                      name="Вывод"
                      animationDuration={300}
                      dot={{
                        fill: '#ec4899',
                        stroke: '#ffffff',
                        strokeWidth: 2,
                        r: 5,
                        strokeDasharray: '0'
                      }}
                      activeDot={{
                        fill: '#ffffff',
                        stroke: '#ec4899',
                        strokeWidth: 3,
                        r: 7
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Конец блока аналитики */}
    </div>
  );
};

export default Dashboard; 